import { Link } from "react-router-dom";
import { useEvent } from "@/context/EventContext";
import { getCountry } from "@/lib/geo";
import { Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";

const routeSeg = (f) => ({ registry: "setup", planning: "tasks", invites: "guests", guests: "guests" }[f] || f);
// capability key -> purchasable bundle key
const BUNDLE_OF = { tasks: "planning", budget: "planning", vendors: "planning", gallery: "guests", thankyou: "guests", invites: "guests", seating: "seating" };

export async function startFeatureCheckout(eapi, eventId, feature, routeKey) {
  try {
    const country = await getCountry();
    const { data } = await eapi.post("/payments/checkout", { origin_url: window.location.origin, feature, ...(country ? { country } : {}) });
    localStorage.setItem("pending_feature_route", `/e/${eventId}/${routeSeg(routeKey || feature)}`);
    window.location.href = data.checkout_url;
  } catch (e) {
    toast.error("Could not start checkout. Please try again.");
  }
}

export function LockBanner({ featureKey }) {
  const { features, eapi, eventId } = useEvent();
  if (!features) return null;
  const buyKey = BUNDLE_OF[featureKey] || featureKey;
  const meta = (features?.catalog || []).find((f) => f.key === buyKey) || { name: featureKey, desc: "", price: 5 };
  const priceLabel = `$${meta.price ?? 5} ${features?.currency || "USD"}`;
  return (
    <div data-testid={`lock-banner-${featureKey}`}
      className="flex flex-col sm:flex-row sm:items-center gap-4 bg-[#2C2A28] text-white rounded-xl px-6 py-5">
      <div className="w-10 h-10 rounded-full bg-white/10 grid place-items-center shrink-0"><Lock size={18} /></div>
      <div className="flex-1">
        <p className="overline text-white/50">Premium add-on</p>
        <p className="font-serif text-xl mt-0.5">{meta.name}</p>
        <p className="text-sm text-white/70 mt-0.5">{meta.desc}</p>
      </div>
      <div className="flex flex-col items-stretch sm:items-end gap-1.5">
        <button onClick={() => startFeatureCheckout(eapi, eventId, buyKey, featureKey)} data-testid={`unlock-${featureKey}-btn`}
          className="flex items-center justify-center gap-2 rounded-full bg-white text-stone-900 px-6 py-2.5 text-sm font-medium hover:bg-[#D4C5B3] transition-colors whitespace-nowrap">
          <Sparkles size={15} /> Unlock for {priceLabel}
        </button>
        <Link to={`/e/${eventId}/pricing?highlight=${buyKey}`} data-testid={`compare-${featureKey}-link`}
          className="text-xs text-white/60 hover:text-white/90 text-center sm:text-right transition-colors">Compare all add-ons →</Link>
      </div>
    </div>
  );
}

export function Gate({ featureKey, children }) {
  const { features } = useEvent();
  if (!features) return <div className="p-16 text-center font-serif text-2xl text-stone-300" data-testid="gate-loading">Loading…</div>;
  if ((features.unlocked || []).includes(featureKey)) return children;
  return (
    <>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 lg:pt-10"><LockBanner featureKey={featureKey} /></div>
      <div className="pointer-events-none opacity-50 select-none" aria-disabled="true" data-testid={`locked-content-${featureKey}`}>{children}</div>
    </>
  );
}
